import React, { memo, useEffect, useRef, useState } from "react"

import "../styles/techMarquee.scss"

/**
 * THE TECH STRIP, SITEWIDE.
 *
 * It used to live inside the three CTA banners (WEB_/MOB_/AI_CtaBanner), which
 * put it on five pages and nowhere else — and with three different lists, so
 * what a visitor saw depended on which page they happened to land on. It is one
 * strip now, attached to the top of the footer, so it appears on every page.
 *
 * The list is the web one from those three: it is the broadest and it is the
 * general stack rather than a discipline's. Both narrower lists are gone with
 * the strips that carried them; if either is worth keeping, it belongs on its
 * own service page as content rather than in a sitewide footer.
 *
 * PAUSED WHEN OFF-SCREEN, and that is the whole reason this is a component
 * rather than markup pasted into the footer. The original animated
 * unconditionally, which was survivable when it appeared on five pages and sat
 * near their end. In the footer of EVERY page it would run off-screen for the
 * entire session on every page load — the exact regression the landing page was
 * fixed for, where an ungated marquee cost 4547ms of main-thread time while
 * completely out of view. A paused CSS animation is not ticked at all.
 */

// The three copies exist to make the loop seamless: the keyframe translates by
// exactly one third, so when it wraps, copy two sits where copy one began.
const COPIES = 3

const ITEMS = [
  "React.js", "Next.js", "Node.js", "TypeScript", ".Net Core", "python",
  "PostgreSQL", "GraphQL", "AWS", "Docker", "Kubernetes", "CI/CD",
  "Microservices", "Serverless", "AI/ML",
]

const TechMarquee = () => {
  const ref = useRef(null)
  // Paused until proven on-screen, not the other way round.
  const [paused, setPaused] = useState(true)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    if (!("IntersectionObserver" in window)) {
      setPaused(false) // no observer: animate rather than never animate
      return
    }

    const observer = new IntersectionObserver(
      entries => entries.forEach(e => setPaused(!e.isIntersecting)),
      { rootMargin: "200px" } // start just before it scrolls into view
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={paused ? "tm is-paused" : "tm"}
      aria-label="Technologies we build with"
    >
      <div className="tm__track">
        {Array.from({ length: COPIES }, (_, copy) => (
          <ul
            className="tm__list"
            key={copy}
            // Only the first copy is real content. The other two are there for
            // the seam, and a screen reader reading forty-five technology names
            // in the footer of every page is noise.
            aria-hidden={copy > 0 ? "true" : undefined}
          >
            {ITEMS.map(item => (
              <li className="tm__item" key={item}>
                {item}
                <span className="tm__star" aria-hidden="true">
                  &#10039;
                </span>
              </li>
            ))}
          </ul>
        ))}
      </div>
    </div>
  )
}

export default memo(TechMarquee)
